import React from 'react';
import { useAuth } from '../AuthContext';

const LandingPage = ({ onGetStarted }) => {
  const { login } = useAuth();

  return (
    <div className="bg-slate-50 min-h-screen font-sans text-slate-900">
      {/* 1. Navigation */}
      <nav className="flex justify-between items-center px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-black tracking-tight text-indigo-600">bane</h1>
        <button 
          onClick={login}
          className="bg-white border border-slate-200 px-5 py-2 rounded-full font-medium hover:bg-slate-50 transition"
        >
          Sign In
        </button>
      </nav>

      {/* 2. Hero Section */}
      <header className="max-w-4xl mx-auto text-center pt-20 pb-16 px-4">
        <span className="bg-indigo-50 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 inline-block">
          Beta is live
        </span>
        <p className="text-xs md:text-sm font-black uppercase tracking-widest text-indigo-600 mb-2">
          Household Logistics
        </p>
        <h2 className="text-5xl md:text-6xl font-extrabold mb-6 tracking-tight leading-tight">
          The bane of your existence, <span className="text-indigo-600">simplified.</span>
        </h2>
        <p className="text-xl text-slate-500 mb-10 leading-relaxed max-w-3xl mx-auto">
          Because "marriage" shouldn’t turn your relationship into a bootstrapped startup managed by two 
          deeply fatigued co-CEOs who never get a performance review or a vacation day.
        </p>
        <div className="inline-flex flex-col items-center gap-3">
          <button 
            onClick={login}
            className="bg-indigo-600 text-white px-10 py-4 rounded-2xl text-lg font-bold hover:bg-indigo-700 shadow-xl shadow-indigo-200 transition-all transform hover:-translate-y-1"
          >
            Get started for free
          </button>
        </div>
      </header>

      {/* Feature Section 1: The Card Matrix */}
      <section className="max-w-7xl mx-auto px-8 py-24 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <span className="text-indigo-600 font-bold uppercase tracking-widest text-xs">The "Not-a-Todo-List"</span>
          <h3 className="text-4xl font-bold mb-6 mt-2 tracking-tight">The Card Matrix</h3>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            We threw out the linear checklist because we know you aren't actually going to "finish" 
            managing this house until you die or sell it. Welcome to <strong>The Vault</strong>.
          </p>
          
          <div className="space-y-6">
            <div className="space-y-6">
                {/* 1. Routine Rotations (The Token Passer) */}
                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                    <div className="bg-indigo-50 p-2 rounded-xl text-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.656 48.656 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3M4.5 12a48.674 48.674 0 00.138 3.662 4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l-3 3m3-3l3 3" />
                    </svg>
                    </div>
                    <div>
                    <p className="font-bold text-slate-800">Routine Rotations (The Token Passer)</p>
                    <p className="text-sm text-slate-500 mt-1">
                        Tired of playing Chore Chicken? The app assigns the task, tracks whose turn it is, 
                        and passes the token. No nagging required. The app says it’s your turn, Brian. Take 
                        it up with the server.
                    </p>
                    </div>
                </div>
                
                {/* 2. The Honey-Do Queue (With Spousal Spits) */}
                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                    <div className="bg-indigo-50 p-2 rounded-xl text-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0M3.124 7.5A8.969 8.969 0 015.292 3m13.416 0a8.969 8.969 0 012.168 4.5" />
                    </svg>
                    </div>
                    <div>
                    <p className="font-bold text-slate-800">The Honey-Do Queue (With Spousal Spits)</p>
                    <p className="text-sm text-slate-500 mt-1">
                        Got a project sitting on the back burner since the Obama administration? Drop it in the queue, 
                        add an email, and let automated weekly reminders do the nagging. You get to be the good guy; 
                        our robot takes the blame.
                    </p>
                    </div>
                </div>

                {/* 3. The Historical Vault */}
                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                    <div className="bg-indigo-50 p-2 rounded-xl text-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                    </svg>
                    </div>
                    <div>
                    <p className="font-bold text-slate-800">The Historical Vault</p>
                    <p className="text-sm text-slate-500 mt-1">
                        Packing lists for camping trips you swear you'll never go on again, and manual links 
                        to the exact furnace filter size you forget every single six months.
                    </p>
                    </div>
                </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-indigo-500/10 rounded-full blur-3xl"></div>
          <MockTaskList />
        </div>
      </section>

      {/* Feature Section 2: Meal Matrix */}
      <section className="max-w-7xl mx-auto px-8 py-24 grid md:grid-cols-2 gap-16 items-center">
        <div className="relative order-last md:order-first">
          <div className="absolute -inset-4 bg-orange-500/10 rounded-full blur-3xl"></div>
          <MockMealPlan />
        </div>

        <div>
          <span className="text-orange-600 font-bold uppercase tracking-widest text-xs">The Drag-and-Drop Meal Plan</span>
          <h3 className="text-4xl font-bold mb-6 mt-2 tracking-tight">The Meal Matrix</h3>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Because answering "What's for dinner?" 5,000 times a week is a human rights violation.
          </p>
          <div className="space-y-6">
            <div className="space-y-6">
            {/* 1. Visual Meal Blocks Icon: Grid / Layered Dashed Layout Blocks */}
            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                <div className="bg-orange-50 p-2 rounded-xl text-orange-600 flex items-center justify-center shadow-sm shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
                </div>
                <div>
                <p className="font-bold text-slate-800">Visual Meal Blocks</p>
                <p className="text-sm text-slate-500 mt-1">
                    Plot out your main dishes and side options for the week without losing your mind.
                </p>
                </div>
            </div>

            {/* 2. The Panic Swap Icon: Dynamic Horizontal Swap / Intersecting Arrows */}
            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                <div className="bg-orange-50 p-2 rounded-xl text-orange-600 flex items-center justify-center shadow-sm shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                </svg>
                </div>
                <div>
                <p className="font-bold text-slate-800">The Panic Swap</p>
                <p className="text-sm text-slate-500 mt-1">
                    Tuesday falling apart? Drag Monday's leftovers onto Wednesday and slide the takeout card into tonight's slot. 
                    No breaking the layout, no breaking your sanity.
                </p>
                </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section 3: Central Command Hub */}
        <section className="max-w-7xl mx-auto px-8 py-24 grid md:grid-cols-2 gap-16 items-center">
        <div>
            <span className="text-purple-600 font-bold uppercase tracking-widest text-xs">Unified Command Center</span>
            <h3 className="text-4xl font-bold mb-6 mt-2 tracking-tight">One source of truth for the human dependents.</h3>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Stop digging through broken group chats, crumpled permission slips, and three separate calendar apps just to figure out where your children are supposed to be. Put the entire household itinerary into a single, cohesive operational dashboard. 
            </p>
            
            <div className="space-y-6">
            {/* 1. Integrated Master Timeline */}
            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                <div className="bg-purple-50 p-2 rounded-xl text-purple-600 flex items-center justify-center shadow-sm shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
                </svg>
                </div>
                <div>
                <p className="font-bold text-slate-800">The Master Household Timeline</p>
                <p className="text-sm text-slate-500 mt-1">
                    Layer gymnastics practice, dentist appointments, and school spirit days into one chronological timeline. You actually get to know who needs to be dropped off where, and when.
                </p>
                </div>
            </div>

            {/* 2. Dependent Critical Info Asset */}
            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                <div className="bg-purple-50 p-2 rounded-xl text-purple-600 flex items-center justify-center shadow-sm shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5a2.25 2.25 0 002.25 2.25zm10.5-11.25h.008v.008H15V8.25zm0 3h.008v.008H15v-.008zm0 3h.008v.008H15v-.008z" />
                </svg>
                </div>
                <div>
                <p className="font-bold text-slate-800">The Vital Info Deposit</p>
                <p className="text-sm text-slate-500 mt-1">
                    Keep track of clothing sizes, allergy notes, and teacher emails in one central digital drawer. No more frantically texting your partner from the Target shoe aisle asking, "What size is Sarah wearing right now?"
                </p>
                </div>
            </div>
            </div>
        </div>

        <div className="relative">
            <div className="absolute -inset-4 bg-purple-500/10 rounded-full blur-3xl"></div>
            <MockKidHub />
        </div>
        </section>

      {/* 3. Social Proof / Reality Check */}
      <section className="bg-slate-900 text-white py-24 border-y border-slate-800">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <span className="text-indigo-400 font-bold uppercase tracking-widest text-xs">🧐 Why This App?</span>
          <blockquote className="text-2xl md:text-3xl font-medium italic my-8 text-slate-100 border-l-4 border-indigo-500 pl-4 md:pl-8 text-left max-w-2xl mx-auto">
            "Most family organizers look like a pastel-colored Pinterest board where everyone is smiling. This app looks like reality."
          </blockquote>
          <p className="text-slate-400 text-lg max-w-xl mx-auto leading-relaxed">
            Other apps try to gamify chores or make grocery shopping feel like a fun family team-building exercise. We don't. We know that running a house is a relentless, rotating wheel of logistics, laundry, and leaking pipes.
            <br /><br />
            We don't make it fun. We just make it stop taking up so much space in your brain.
          </p>
        </div>
      </section>

      {/* 4. Final CTA Section */}
      <section className="max-w-4xl mx-auto px-8 py-24 text-center">
        <div className="bg-indigo-600 rounded-[3rem] p-12 md:p-20 shadow-2xl shadow-indigo-200 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          
          <h3 className="text-3xl md:text-5xl font-bold text-white mb-6 relative z-10">
            Ready to reclaim your <br className="hidden md:block" /> family's time?
          </h3>
          <button 
            onClick={login}
            className="bg-white text-indigo-600 px-10 py-4 rounded-2xl text-lg font-bold hover:bg-slate-50 transition-all transform hover:scale-105 active:scale-95 shadow-xl relative z-10"
          >
            CLAIM MY SANITY
          </button>
          <p className="text-indigo-200 text-xs mt-4 relative z-10 italic">(Or at least automate the trash reminder)</p>
        </div>
        <p className="mt-12 text-slate-400 text-sm font-medium">
          © 2026 bane. All rights reserved.
        </p>
      </section>
    </div>
  );
};

// UI Preview Component Mocks
const MockTaskList = () => {
  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden max-w-lg mx-auto transform rotate-2 hover:rotate-0 transition-all duration-500">
      <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
        <span className="font-bold text-slate-800">The Vault Matrix</span>
        <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">Brian's Turn</span>
      </div>
      <div className="p-6 space-y-4">
        <div className="p-3 rounded-xl border bg-white shadow-sm flex justify-between items-center">
          <div>
            <p className="font-semibold text-sm">Take out the heavy trash</p>
            <p className="text-[10px] text-slate-400 font-bold uppercase">Token Owner: Brian</p>
          </div>
          <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-1 rounded font-mono">Routine Rotation</span>
        </div>
        <div className="p-3 rounded-xl border bg-white shadow-sm flex justify-between items-center">
          <div>
            <p className="font-semibold text-sm">Fix deck step (Since 2012)</p>
            <p className="text-[10px] text-orange-400 font-bold uppercase">Weekly Nags: Sent</p>
          </div>
          <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-1 rounded font-mono">Honey-Do Queue</span>
        </div>
        <div className="p-3 rounded-xl border bg-slate-50 opacity-75 flex justify-between items-center">
          <div>
            <p className="font-semibold text-sm text-slate-600">Furnace Filter Link (Size 16x25x1)</p>
            <p className="text-[10px] text-slate-400">Stored forever</p>
          </div>
          <span className="text-[10px] bg-slate-200 text-slate-600 px-2 py-1 rounded font-mono">Historical</span>
        </div>
      </div>
    </div>
  );
};

const MockMealPlan = () => {
  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden max-w-lg mx-auto transform -rotate-1 hover:rotate-0 transition-all duration-500">
      <div className="bg-orange-500 p-4 text-white flex justify-between items-center">
        <h4 className="font-bold text-sm">Drag-and-Drop Meal Matrix</h4>
        <span className="text-xs bg-orange-600 px-2 py-1 rounded">Panic Mode Enabled</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="p-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-center text-xs text-slate-400 font-medium">
          ← Drag Monday's Leftovers here
        </div>
        <div className="p-3 rounded-xl border border-orange-200 bg-orange-50">
          <div className="flex justify-between text-[10px] font-bold text-orange-600 uppercase mb-1">
            <span>Tuesday (Tonight)</span>
            <span className="bg-orange-200 px-1.5 py-0.5 rounded">🚨 Panic Swap</span>
          </div>
          <p className="font-bold text-sm text-slate-800">Emergency Takeout Card</p>
          <p className="text-xs text-slate-500 italic">Side: Delivery Guy Sanity</p>
        </div>
      </div>
    </div>
  );
};

const MockKidHub = () => {
  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden max-w-lg mx-auto transform rotate-1 hover:rotate-0 transition-all duration-500">
      {/* Header: Focused on the Dependent Asset Profile */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-5 text-white flex justify-between items-center">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest opacity-75">Dependent Profile</p>
          <h4 className="text-lg font-bold">Sarah's Logistics Card</h4>
        </div>
        <span className="text-xs bg-white/20 px-2 py-1 rounded font-mono border border-white/25">Age: 8</span>
      </div>

      <div className="p-5 space-y-5">
        {/* Section 1: Today's High-Stakes Itinerary */}
        <div>
          <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Today's Dispatch</h5>
          <div className="space-y-2">
            <div className="flex items-center gap-4 p-3 rounded-xl bg-purple-50 border border-purple-100">
              <div className="bg-white rounded-lg p-2 min-w-[55px] text-center shadow-sm shrink-0">
                <p className="text-[10px] font-bold text-purple-600">3:15 PM</p>
              </div>
              <div>
                <p className="font-bold text-slate-800 text-xs">Bus Drop-off / Snack Protocol</p>
                <p className="text-[10px] text-purple-500">Note: Hangry risk high.</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-3 rounded-xl bg-purple-50 border border-purple-100">
              <div className="bg-white rounded-lg p-2 min-w-[55px] text-center shadow-sm shrink-0">
                <p className="text-[10px] font-bold text-purple-600">5:00 PM</p>
              </div>
              <div>
                <p className="font-bold text-slate-800 text-xs">Gymnastics (Car pool with Lucas)</p>
                <p className="text-[10px] text-purple-500">Driver Assignment: Partner's turn</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Vital Info Ledger */}
        <div>
          <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Vital Info Ledger</h5>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">👟 Shoe Size</p>
              <p className="font-bold text-slate-800 mt-0.5">Youth 2 (Last checked: Today)</p>
            </div>
            <div className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">🏫 Teacher / Locker</p>
              <p className="font-bold text-slate-800 mt-0.5">Mrs. Gable / #142</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;